import { createdContractPath } from "./contractInstallationIntent";

// Pure wizard logic for the New Contract dialog.
// Extracted so it can be unit-tested without DOM (repo convention: node env,
// no jsdom/testing-library). The component NewContractDialog imports these so
// tests reflect the real logic used at runtime.

export const GENERIC_SUBMIT_ERROR =
  "No se pudo crear el contrato. Revisa los campos e inténtalo de nuevo.";
export const TITLE_REQUIRED_ERROR = "El título del contrato es obligatorio.";
export const DATE_ORDER_ERROR = "La fecha de fin no puede ser anterior a la de inicio.";
export const MIXED_PCT_ERROR =
  "La estrategia mixta requiere un porcentaje de certificaciones entre 1 y 99.";

export const CONTRACT_ERROR_MAP: Record<string, string> = {
  FORBIDDEN: "No tienes permiso para crear contratos o fijar datos económicos.",
  CUSTOMER_PARTNER_NOT_FOUND: "El cliente no existe.",
  CUSTOMER_PARTNER_INACTIVE: "El cliente está inactivo.",
  CUSTOMER_PARTNER_MERGED: "El cliente está fusionado con otro.",
  CUSTOMER_PARTNER_NOT_A_CUSTOMER: "El cliente no es un cliente válido.",
  BILLING_PARTNER_NOT_FOUND: "La entidad de facturación no existe.",
  BILLING_PARTNER_INACTIVE: "La entidad de facturación está inactiva.",
  BILLING_PARTNER_MERGED: "La entidad de facturación está fusionada.",
  SALES_OWNER_NOT_FOUND: "El responsable comercial no existe.",
  PM_NOT_FOUND: "El Project Manager no existe.",
  SALES_OWNER_NOT_ELIGIBLE: "El responsable comercial no es válido para este rol.",
  PM_NOT_ELIGIBLE: "El Project Manager no es válido para este rol.",
  CONTRACT_CAPABILITY_DENIED: "No tienes permiso para esta operación.",
  WRITE_ECONOMIC_REQUIRED: "No tienes permiso para fijar datos económicos.",
  CONTRACT_BILLING_SCHEMA_NOT_READY:
    "El sistema de facturación no está listo. Inténtalo de nuevo en unos minutos.",
  BILLING_STRATEGY_MIXED_REQUIRES_VALID_PCT: MIXED_PCT_ERROR,
};

// Maps server error codes (arriving in TRPCClientError.message) to the wizard
// step that should handle them.
export const ERROR_STEP: Record<string, number> = {
  CUSTOMER_PARTNER_NOT_FOUND: 1,
  CUSTOMER_PARTNER_INACTIVE: 1,
  CUSTOMER_PARTNER_MERGED: 1,
  CUSTOMER_PARTNER_NOT_A_CUSTOMER: 1,
  SALES_OWNER_NOT_FOUND: 2,
  PM_NOT_FOUND: 2,
  SALES_OWNER_NOT_ELIGIBLE: 2,
  PM_NOT_ELIGIBLE: 2,
  WRITE_ECONOMIC_REQUIRED: 3,
  CONTRACT_BILLING_SCHEMA_NOT_READY: 3,
  BILLING_STRATEGY_MIXED_REQUIRES_VALID_PCT: 3,
  BILLING_PARTNER_NOT_FOUND: 3,
  BILLING_PARTNER_INACTIVE: 3,
  BILLING_PARTNER_MERGED: 3,
};

// The functional code arrives in error.message because the router re-throws
// TRPCError({ code, message: businessCode }). Be tolerant in case the code is
// nested under data/code/cause, to avoid false negatives.
function extractErrorCode(e: unknown): string {
  if (e == null) return "";
  const obj = e as { message?: unknown; data?: { message?: unknown; code?: unknown }; cause?: unknown };
  const candidates: unknown[] = [
    obj.message,
    obj.data?.message,
    obj.data?.code,
    obj.cause && typeof obj.cause === "object"
      ? (obj.cause as { message?: unknown }).message
      : undefined,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 0) return c;
  }
  return "";
}

export function parseServerError(e: unknown): { message: string; step: number } {
  const code = extractErrorCode(e);
  return {
    message: CONTRACT_ERROR_MAP[code] ?? GENERIC_SUBMIT_ERROR,
    step: ERROR_STEP[code] ?? 4,
  };
}

export function requiredTitleError(title: string): string | null {
  return title.trim() ? null : TITLE_REQUIRED_ERROR;
}

export function dateOrderError(start: string | null | undefined, end: string | null | undefined): string | null {
  if (end && start && end < start) return DATE_ORDER_ERROR;
  return null;
}

export function mixedPercentageError(pct: string | null | undefined): string | null {
  if (!pct) return null;
  const n = Number(pct);
  if (Number.isNaN(n) || n < 1 || n > 99) return MIXED_PCT_ERROR;
  return null;
}

// Only show the error on the step that originated it.
export function errorForStep(error: string | null, errorStep: number | null, step: number): string | null {
  return error && errorStep === step ? error : null;
}

// Single-flight guard mirroring the component's submittingRef pattern.
export function createSingleFlight() {
  let held = false;
  return {
    tryAcquire(): boolean {
      if (held) return false;
      held = true;
      return true;
    },
    release(): void {
      held = false;
    },
    isHeld(): boolean {
      return held;
    },
  };
}

export interface ContractCreated {
  id: number;
  contractCode: string;
}

export interface ContractSubmitDeps<T> {
  create(payload: T): Promise<ContractCreated>;
  invalidateList(): Promise<unknown> | unknown;
  navigate(path: string): void;
  onSuccess(created: ContractCreated): void; // reset + close dialog + toast in the component
  onError(message: string, step: number): void;
}

export interface ContractSubmitResult {
  ok: boolean;
  reason: "in-flight" | "done" | "error";
  parsedError?: { message: string; step: number };
}

// Pure orchestrator for the wizard submit. Single-flight ensures a second
// invocation while one is in progress does NOT create a second contract.
export function createContractSubmitController<T extends { customerPartnerId: number }>(
  deps: ContractSubmitDeps<T>,
) {
  const flight = createSingleFlight();
  let submitting = false;

  async function run(payload: T, options: { addInstallation?: boolean } = {}): Promise<ContractSubmitResult> {
    if (!flight.tryAcquire()) {
      return { ok: false, reason: "in-flight" };
    }
    submitting = true;
    try {
      let created: ContractCreated;
      try {
        created = await deps.create(payload);
      } catch (e) {
        const parsedError = parseServerError(e);
        deps.onError(parsedError.message, parsedError.step);
        return { ok: false, reason: "error", parsedError };
      }
      deps.onSuccess(created);
      // A cache-refresh failure is NOT a failed creation: keep the persisted
      // contract and continue to it, never invite a second create attempt.
      try { await deps.invalidateList(); } catch { /* detail loads canonical data */ }
      deps.navigate(createdContractPath(payload.customerPartnerId, created.id, options.addInstallation));
      return { ok: true, reason: "done" };
    } finally {
      submitting = false;
      flight.release();
    }
  }

  return {
    run,
    isSubmitting: () => submitting,
  };
}
